import { useMemo, useState } from 'react';
import { Button, Tooltip } from '@mui/material';

import {
    DataTable,
    Loading,
    ErrorMessage,
    type TableColumn,
    PreviewIcon,
    SimpleTextField,
} from '@/src/ui';
import type { StudentExamAttemptsDTO } from '../models/attemptDetailsDTOs';
import AttemptDetailsModal from './AttemptDetailsModal';
import { ReportCellStyle, ReportHeaderStyle, utcToLocal, formatOptions } from '@/src/utils';

interface AttemptDetailsTableProps {
    data: StudentExamAttemptsDTO[];
    isLoading: boolean;
    error?: string;
    refreshTableData: () => Promise<void>;
}

export default function AttemptDetailsTable({
    data,
    isLoading,
    error,
    refreshTableData,
}: AttemptDetailsTableProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStudent, setSelectedStudent] = useState<StudentExamAttemptsDTO | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const tableData = useMemo(() => {
        const list = data ?? [];
        if (!searchTerm.trim()) return list;
        return list.filter((d) => d.studentId.toString().includes(searchTerm.trim()));
    }, [data, searchTerm]);

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'studentName',
                header: 'Student Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptCount',
                header: 'Attempts',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StudentExamAttemptsDTO) => (row.attempts ?? []).length,
            },
            {
                dataKey: 'lastSubmissionDate',
                header: 'Last Submission',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StudentExamAttemptsDTO) => {
                    const attempts = row.attempts?.filter((a) => a.endDTUTC) ?? [];
                    if (attempts.length === 0) return '—';

                    const lastAttempt = attempts.reduce((latest, current) => {
                        return new Date(current.endDTUTC!) > new Date(latest.endDTUTC!)
                            ? current
                            : latest;
                    });

                    if (!lastAttempt.endDTUTC) return '—';
                    return utcToLocal(lastAttempt.endDTUTC).toLocaleString(
                        undefined,
                        formatOptions
                    );
                },
            },
            {
                dataKey: 'viewAttempt',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: StudentExamAttemptsDTO) => {
                    if (!row.attempts || row.attempts.length === 0) return null;
                    return (
                        <Tooltip title="View Attempts" arrow>
                            <Button
                                size="small"
                                className="icon-button"
                                startIcon={
                                    <PreviewIcon style={{ width: '28px', height: '28px' }} />
                                }
                                onClick={() => {
                                    setSelectedStudent(row);
                                    setModalOpen(true);
                                }}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            ></Button>
                        </Tooltip>
                    );
                },
            },
        ],
        []
    );

    if (isLoading) return <Loading message="Loading assessment details..." />;
    if (error) return <ErrorMessage error={error} />;

    return (
        <>
            <hr className="border-t border-gray-300 my-2" />
            <br />

            <div className="flex justify-end items-center mb-4">
                <SimpleTextField
                    name="Search Exam Attempts"
                    label="Search by student ID..."
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 220 }}
                />
            </div>

            <div className="flex flex-col gap-6 w-full overflow-auto">
                <DataTable
                    columns={columns}
                    data={tableData}
                    tableName="Exam Attempt Details"
                    alternatingRowColor
                    defaultSortColumn="studentName"
                    defaultSortDirection="asc"
                />
            </div>

            {modalOpen && selectedStudent && (
                <AttemptDetailsModal
                    open={modalOpen}
                    onClose={async (changesMade: boolean) => {
                        setModalOpen(false);
                        if (changesMade) {
                            await refreshTableData();
                        }
                    }}
                    student={selectedStudent}
                />
            )}
        </>
    );
}
